import { useMemo, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from '../../../redux/TranslationHelper';
import { HistoryItem } from './CompanyHistory.types';

export interface CompanyHistoryLogicReturn {
  // Translated props
  title: string;
  subtitle?: string;
  timelineStyle: 'vertical' | 'horizontal';
  colorScheme: {
    primary: string;
    secondary: string;
    background: string;
    text: string;
  };
  animation: boolean;
  imageWidth: number;
  imageHeight: number;
  historyItems: HistoryItem[];
  
  // Functions
  addHistoryItem: (item: HistoryItem) => HistoryItem[];
  updateHistoryItem: (id: string, updatedItem: Partial<HistoryItem>) => HistoryItem[];
  deleteHistoryItem: (id: string) => HistoryItem[];
  
  // Utils
  t: (key: string) => string;
}

export const useCompanyHistoryLogic = (props: any): CompanyHistoryLogicReturn => {
  const { t } = useTranslation();
  
  // Extract props with defaults
  const {
    title = t("회사 연혁"),
    subtitle,
    timelineStyle = 'vertical',
    colorScheme = {
      primary: '#3498db',
      secondary: '#f1c40f',
      background: '#ffffff',
      text: '#333333'
    },
    animation = true,
    imageWidth = 150,
    imageHeight = 150,
    historyItems = [],
  } = props.data || props;

  // 타임라인 항목 추가
  const addHistoryItem = useCallback((item: HistoryItem) => {
    return [...historyItems, { ...item, id: uuidv4() }];
  }, [historyItems]);

  // 타임라인 항목 업데이트
  const updateHistoryItem = useCallback((id: string, updatedItem: Partial<HistoryItem>) => {
    return historyItems.map((item: any) =>
      item.id === id ? { ...item, ...updatedItem } : item
    );
  }, [historyItems]);

  // 타임라인 항목 삭제
  const deleteHistoryItem = useCallback((id: string) => {
    return historyItems.filter((item: any) => item.id !== id);
  }, [historyItems]);

  return {
    // Props
    title,
    subtitle,
    timelineStyle,
    colorScheme,
    animation,
    imageWidth,
    imageHeight,
    historyItems,
    
    // Functions
    addHistoryItem,
    updateHistoryItem,
    deleteHistoryItem,
    
    // Utils
    t
  };
};